import type { PlayerIndex, ScreenName, SimStateSnapshot } from '@md/shared';
import type { AudioReactor } from '../audio';
import { createHud, formatStars, formatTime } from '../game/hud';
import { createRenderer } from '../game/renderer';
import type { GameResult, GameSession } from '../game/session';
import {
  CONTROL_HINTS,
  createKeyboardController,
  KEY_BINDINGS,
  type KeyBinding,
} from '../input/keyboard';
import { createTouchControls } from '../input/touch';

/**
 * Écran de jeu : canvas PixiJS + HUD + surimpressions (décompte, pause,
 * résultats) + contrôles tactiles.
 *
 * L'écran ne connaît QUE le contrat `GameSession` : il est donc rigoureusement
 * identique en mode local (1 clavier, 2 joueurs) et en mode réseau (serveur
 * autoritaire + prédiction). En régime manuel (tests locaux), la
 * synchronisation de l'interface reste SYNCHRONE à la sortie de
 * `session.step()`, via l'abonnement à la session.
 */
export interface GameScreen {
  readonly element: HTMLElement;
  /** (Re)branche l'écran sur une session — local ou réseau. */
  configure(config: GameScreenConfig): void;
  mount(): void;
  unmount(): void;
  /** Affiche (ou masque) le décompte 3-2-1. */
  setCountdown(seconds: number | null): void;
  /** Affiche (ou masque) la surimpression de pause. */
  setPaused(paused: boolean, message?: string): void;
  /** Affiche l'écran de résultats à partir d'un résultat autoritaire. */
  showResult(result: GameResult | null): void;
}

export interface GameScreenConfig {
  readonly session: GameSession;
  readonly mode: 'local' | 'net';
  /** Table clavier ; par défaut le clavier partagé à deux joueurs. */
  readonly bindings?: Readonly<Record<string, KeyBinding>>;
  /** Robot piloté par les contrôles tactiles. */
  readonly touchPlayer?: PlayerIndex;
  /** Aide affichée sous le HUD. */
  readonly hints?: readonly { readonly who: string; readonly keys: string }[];
  /** Bouton « Rejouer » de l'écran de victoire (`null` = masqué). */
  readonly onReplay?: (() => void) | null;
  /**
   * Bouton « ↺ Recommencer » de la barre d'outils — relance le niveau en
   * PLEINE partie, pour un duo bloqué (`null` = masqué).
   */
  readonly onRestart?: (() => void) | null;
  readonly onExit: () => void;
  /**
   * Bouton « ← Accueil » de la barre d'outils. En réseau il QUITTE la room,
   * là où `onExit` (écran de résultats) se contente de revenir au salon.
   */
  readonly onQuit?: () => void;
  /** Libellé du bouton de sortie de l'écran de résultats. */
  readonly exitLabel?: string;
  /** Niveau joué — sert à enregistrer la progression à la victoire. */
  readonly levelId?: string;
  /** Appelé une fois quand la partie est gagnée (résultat autoritaire). */
  readonly onWin?: (result: GameResult, levelId: string | undefined) => void;
  /** Bouton « Niveau suivant » de l'écran de victoire (`null` = masqué). */
  readonly onNext?: (() => void) | null;
  /** Badge affiché dans la barre d'outils (code de room en réseau). */
  readonly roomLabel?: string;
}

export interface GameScreenOptions {
  /** Remonte les changements d'écran ("game" ↔ "results") à l'application. */
  readonly onScreen: (screen: ScreenName) => void;
  /**
   * Pont état de simulation → sons et vibrations (concept §9).
   *
   * Il ne fait qu'OBSERVER les instantanés : la simulation reste pure.
   */
  readonly audio?: AudioReactor;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

export function createGameScreen(options: GameScreenOptions): GameScreen {
  const element = el('main', 'screen screen--game');
  const hud = createHud();
  const renderer = createRenderer();

  const stage = el('div', 'game-stage');
  stage.append(renderer.element);

  // --- Décompte 3-2-1 ------------------------------------------------------
  const countdown = el('div', 'overlay overlay--countdown');
  countdown.dataset.testid = 'countdown';
  countdown.hidden = true;
  const countdownValue = el('span', 'overlay__count');
  countdownValue.dataset.testid = 'countdown-value';
  countdown.append(countdownValue, el('p', 'overlay__hint', 'Préparez-vous…'));
  stage.append(countdown);

  // --- Pause ---------------------------------------------------------------
  const pause = el('div', 'overlay overlay--pause');
  pause.dataset.testid = 'paused';
  pause.hidden = true;
  const pauseMessage = el('p', 'overlay__hint', '');
  pauseMessage.dataset.testid = 'paused-message';
  pause.append(el('h2', 'overlay__title', 'Partie en pause'), pauseMessage);
  stage.append(pause);

  // --- Écran de victoire ---------------------------------------------------
  const victory = el('div', 'victory');
  victory.dataset.testid = 'victory';
  victory.hidden = true;
  const victoryTime = el('span', 'victory__value');
  victoryTime.dataset.testid = 'victory-time';
  const victoryDeaths = el('span', 'victory__value');
  victoryDeaths.dataset.testid = 'victory-deaths';
  const victoryBolts = el('span', 'victory__value');
  victoryBolts.dataset.testid = 'victory-bolts';
  const victoryStars = el('div', 'victory__stars');
  victoryStars.dataset.testid = 'victory-stars';
  const replayButton = el('button', 'button button--primary', 'Rejouer');
  replayButton.type = 'button';
  replayButton.dataset.testid = 'victory-replay';
  const homeButton = el('button', 'button', 'Retour à l’accueil');
  homeButton.type = 'button';
  homeButton.dataset.testid = 'victory-home';
  const nextButton = el('button', 'button button--primary', 'Niveau suivant →');
  nextButton.type = 'button';
  nextButton.dataset.testid = 'victory-next';
  nextButton.hidden = true;

  const victoryPanel = el('div', 'victory__panel');
  const stats = el('div', 'victory__stats');
  const timeRow = el('div', 'victory__row');
  timeRow.append(el('span', 'victory__label', 'Temps'), victoryTime);
  const deathRow = el('div', 'victory__row');
  deathRow.append(el('span', 'victory__label', 'Morts'), victoryDeaths);
  const boltRow = el('div', 'victory__row');
  boltRow.append(el('span', 'victory__label', 'Boulons'), victoryBolts);
  stats.append(timeRow, boltRow, deathRow);
  const actions = el('div', 'victory__actions');
  actions.append(nextButton, replayButton, homeButton);
  victoryPanel.append(
    el('h2', 'victory__title', 'Niveau terminé !'),
    el('p', 'victory__subtitle', 'Polo et Mag ont rejoint leurs portes.'),
    victoryStars,
    stats,
    actions,
  );
  victory.append(victoryPanel);
  stage.append(victory);

  const quitButton = el('button', 'button button--ghost', '← Accueil');
  quitButton.type = 'button';
  quitButton.dataset.testid = 'quit';
  const restartButton = el('button', 'button button--ghost', '↺ Recommencer');
  restartButton.type = 'button';
  restartButton.dataset.testid = 'restart-level';
  const toolbar = el('div', 'game-toolbar');
  const roomBadge = el('span', 'game-toolbar__room');
  roomBadge.dataset.testid = 'game-room';
  roomBadge.hidden = true;
  toolbar.append(quitButton, restartButton, roomBadge);

  element.append(toolbar, hud.element, stage);

  // --- Câblage -------------------------------------------------------------
  let config: GameScreenConfig | null = null;
  let won = false;

  const keyboard = createKeyboardController({
    onInput: (player, patch) => {
      config?.session.setInputs(player, patch);
    },
  });

  const touch = createTouchControls({
    onInput: (player, patch) => {
      config?.session.setInputs(player, patch);
    },
  });
  stage.append(touch.element);

  const showVictory = (result: GameResult | null): void => {
    const wasWon = won;
    won = result !== null;
    victory.hidden = !won;
    if (result) {
      victoryTime.textContent = formatTime(result.timeMs);
      victoryDeaths.textContent = String(result.deaths);
      victoryBolts.textContent = `${result.bolts.collected}/${result.bolts.total}`;
      victoryStars.textContent = formatStars(result.stars);
      victoryStars.dataset.stars = String(result.stars);
      if (!wasWon) {
        // Une seule fois par victoire : c'est ce qui enregistre la progression.
        config?.onWin?.(result, config.levelId);
      }
    }
    options.onScreen(won ? 'results' : 'game');
  };

  const sync = (snapshot: SimStateSnapshot): void => {
    hud.update(snapshot);
    options.audio?.observe(snapshot);
    if (config?.mode !== 'local') {
      // En réseau, la victoire est annoncée par le serveur (`gameEnd`), pas
      // déduite de la prédiction locale.
      return;
    }
    if (snapshot.won === won) {
      return;
    }
    showVictory(snapshot.won ? config.session.result() : null);
  };

  replayButton.addEventListener('click', () => config?.onReplay?.());
  restartButton.addEventListener('click', () => config?.onRestart?.());
  nextButton.addEventListener('click', () => config?.onNext?.());
  homeButton.addEventListener('click', () => config?.onExit());
  quitButton.addEventListener('click', () => (config?.onQuit ?? config?.onExit)?.());

  let unsubscribe: (() => void) | null = null;
  let frame: number | null = null;
  let lastTime = 0;

  const loop = (time: number): void => {
    const elapsed = lastTime === 0 ? 0 : time - lastTime;
    lastTime = time;
    config?.session.advance(elapsed);
    renderer.draw(config?.session.state() ?? null, elapsed);
    frame = requestAnimationFrame(loop);
  };

  const onResize = (): void => touch.refresh();

  return {
    element,

    configure(next: GameScreenConfig): void {
      // Changer de session en cours de route : on débranche l'ancienne AVANT
      // de basculer, sinon l'abonnement et le clavier resteraient collés
      // dessus. L'appelant remonte l'écran juste après.
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
        keyboard.detach();
        touch.detach();
        window.removeEventListener('resize', onResize);
        if (frame !== null) {
          cancelAnimationFrame(frame);
          frame = null;
        }
      }
      config = next;
      // Table par défaut : clavier partagé à deux joueurs (concept §5.1).
      keyboard.setBindings(next.bindings ?? KEY_BINDINGS);
      touch.setPlayer(next.touchPlayer ?? 0);
      replayButton.hidden = !next.onReplay;
      restartButton.hidden = !next.onRestart;
      nextButton.hidden = !next.onNext;
      homeButton.textContent = next.exitLabel ?? 'Retour à l’accueil';
      options.audio?.reset();
      hud.setHints(next.hints ?? CONTROL_HINTS);
      roomBadge.hidden = next.roomLabel === undefined;
      roomBadge.textContent = next.roomLabel ?? '';
      won = false;
      victory.hidden = true;
      countdown.hidden = true;
      pause.hidden = true;
    },

    mount(): void {
      if (unsubscribe || !config) {
        return;
      }
      unsubscribe = config.session.subscribe(sync);
      keyboard.attach();
      touch.attach();
      window.addEventListener('resize', onResize);
      const snapshot = config.session.snapshot();
      if (snapshot) {
        won = !snapshot.won;
        sync(snapshot);
      }
      lastTime = 0;
      frame = requestAnimationFrame(loop);
    },

    unmount(): void {
      unsubscribe?.();
      unsubscribe = null;
      keyboard.detach();
      touch.detach();
      window.removeEventListener('resize', onResize);
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    },

    setCountdown(seconds: number | null): void {
      countdown.hidden = seconds === null;
      countdownValue.textContent = seconds === null ? '' : String(Math.max(0, seconds));
      countdown.dataset.seconds = seconds === null ? '' : String(seconds);
    },

    setPaused(value: boolean, message = ''): void {
      pause.hidden = !value;
      pauseMessage.textContent = message;
    },

    showResult(result: GameResult | null): void {
      showVictory(result);
    },
  };
}
